import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Search,
  Users,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Eye,
  Edit,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Heart
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

const TIPOS_REFERIDO = {
  novio: { label: 'Novio', color: 'bg-[#D4AF37]', comision: '5%' },
  invitado: { label: 'Invitado', color: 'bg-blue-600', comision: '3%' },
  mascota: { label: 'Mascota', color: 'bg-pink-600', comision: '3%' },
  paje: { label: 'Paje', color: 'bg-purple-600', comision: '3%' },
  corte_completa: { label: 'Corte Completa', color: 'bg-emerald-600', comision: '5%' },
  otro: { label: 'Otro', color: 'bg-zinc-600', comision: '2%' }
};

const ESTADOS_REFERIDO = {
  registrado: { label: 'Registrado', color: 'bg-zinc-600', icon: Clock },
  pendiente_contacto: { label: 'Pendiente contacto', color: 'bg-amber-600', icon: Clock },
  contactado: { label: 'Contactado', color: 'bg-blue-600', icon: CheckCircle },
  cotizado: { label: 'Cotizado', color: 'bg-purple-600', icon: FileText },
  pendiente_ficha: { label: 'Pendiente ficha', color: 'bg-orange-600', icon: Clock },
  ficha_creada: { label: 'Ficha creada', color: 'bg-cyan-600', icon: CheckCircle },
  venta_realizada: { label: 'Venta realizada', color: 'bg-green-600', icon: CheckCircle },
  no_fue_venta: { label: 'No fue venta', color: 'bg-red-600', icon: XCircle },
  pendiente_entrega: { label: 'Pendiente entrega', color: 'bg-indigo-600', icon: Clock },
  listo_cobro: { label: 'Listo para cobro', color: 'bg-[#D4AF37]', icon: DollarSign },
  cuenta_cobro_generada: { label: 'Cuenta cobro generada', color: 'bg-teal-600', icon: FileText },
  pagado: { label: 'Pagado', color: 'bg-emerald-600', icon: CheckCircle },
  rechazado: { label: 'Rechazado', color: 'bg-red-800', icon: XCircle }
};

const ReferidosList = () => {
  const navigate = useNavigate();
  const { aliado_id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referidos, setReferidos] = useState([]);
  const [aliado, setAliado] = useState(null);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchAliado = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados/${aliado_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliado(response.data);
    } catch (error) {
      console.debug('Error fetching aliado');
    }
  }, [aliado_id]);

  const fetchReferidos = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      
      const response = await axios.get(`${API_URL}/api/aliados/${aliado_id}/referidos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let data = response.data.referidos || [];
      
      // Filtrar localmente por búsqueda y tipo
      if (search) {
        const searchLower = search.toLowerCase();
        data = data.filter(r => 
          r.nombre?.toLowerCase().includes(searchLower) ||
          r.telefono?.includes(search) ||
          r.correo?.toLowerCase().includes(searchLower)
        );
      }
      if (filterTipo) {
        data = data.filter(r => r.tipo_referido === filterTipo);
      }
      
      setReferidos(data);
    } catch (error) {
      toast.error('Error al cargar los referidos');
    } finally {
      setLoading(false);
    }
  }, [aliado_id, filterEstado, search, filterTipo]);

  useEffect(() => {
    fetchAliado();
    fetchReferidos();
  }, [fetchAliado, fetchReferidos]);

  // Estadísticas
  const stats = {
    total: referidos.length,
    // Pendientes: registrado, pendiente_contacto, contactado, cotizado (no vendidos, no pagados, no anulados)
    pendientes: referidos.filter(r => ['registrado', 'pendiente_contacto', 'contactado', 'cotizado', 'pendiente_ficha', 'ficha_creada'].includes(r.estado)).length,
    // Vendidos: con venta realizada independiente del estado de pago
    vendidos: referidos.filter(r => r.fue_venta === true || ['venta_realizada', 'listo_cobro', 'cuenta_cobro_generada', 'pagado'].includes(r.estado)).length,
    // No venta
    noVenta: referidos.filter(r => r.fue_venta === false || r.estado === 'no_fue_venta').length,
    // Por cobrar: comisiones de referidos vendidos que NO están pagados
    // Considera tanto estado_comision como estado del referido
    comisionPendiente: referidos.filter(r => {
      const tieneComision = (r.valor_comision || 0) > 0;
      const esVenta = r.fue_venta === true || ['venta_realizada', 'listo_cobro', 'cuenta_cobro_generada'].includes(r.estado);
      const noEstaPagado = r.estado_comision !== 'pagada' && r.estado !== 'pagado';
      return tieneComision && esVenta && noEstaPagado;
    }).reduce((sum, r) => sum + (r.valor_comision || 0), 0),
    // Cobrado: comisiones de referidos que YA fueron pagados
    comisionPagada: referidos.filter(r => {
      return r.estado_comision === 'pagada' || r.estado === 'pagado';
    }).reduce((sum, r) => sum + (r.valor_comision || 0), 0)
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-black border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => user?.role === 'aliado' ? navigate('/panel-aliado') : navigate(`/aliados/${aliado_id}`)}
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{user?.role === 'aliado' ? 'Mi Panel' : 'Aliado'}</span>
              </button>
              <div className="h-6 w-px bg-zinc-800" />
              <div>
                <h1 className="font-display text-lg">Referidos</h1>
                {aliado && <p className="text-xs text-zinc-400">{aliado.nombre}</p>}
              </div>
            </div>
            
            <button
              onClick={() => navigate(`/aliados/${aliado_id}/referidos/nuevo`)}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nuevo Referido</span>
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
            <div>
              <p className="text-2xl font-display">{stats.total}</p>
              <p className="text-xs text-zinc-400">Total</p>
            </div>
            <div>
              <p className="text-2xl font-display text-amber-400">{stats.pendientes}</p>
              <p className="text-xs text-zinc-400">Pendientes</p>
            </div>
            <div>
              <p className="text-2xl font-display text-green-400">{stats.vendidos}</p>
              <p className="text-xs text-zinc-400">Vendidos</p>
            </div>
            <div>
              <p className="text-2xl font-display text-red-400">{stats.noVenta}</p>
              <p className="text-xs text-zinc-400">No Venta</p>
            </div>
            <div>
              <p className="text-lg font-display text-[#D4AF37]">{formatPrice(stats.comisionPendiente)}</p>
              <p className="text-xs text-zinc-400">Por Cobrar</p>
            </div>
            <div>
              <p className="text-lg font-display text-emerald-400">{formatPrice(stats.comisionPagada)}</p>
              <p className="text-xs text-zinc-400">Cobrado</p>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filtros */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Búsqueda */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono o correo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white placeholder-zinc-500"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters || filterEstado || filterTipo
                  ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                  : 'border-zinc-700 hover:bg-zinc-800'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {(filterEstado || filterTipo) && (
                <span className="bg-black/20 px-1.5 py-0.5 rounded text-xs">
                  {[filterEstado, filterTipo].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
          
          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800">
              <select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
              >
                <option value="">Todos los tipos</option>
                {Object.entries(TIPOS_REFERIDO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label} ({value.comision})</option>
                ))}
              </select>
              
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white"
              >
                <option value="">Todos los estados</option>
                {Object.entries(ESTADOS_REFERIDO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>
              
              {(filterEstado || filterTipo) && (
                <button
                  onClick={() => { setFilterEstado(''); setFilterTipo(''); }}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : referidos.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <h3 className="font-display text-xl mb-2">No hay referidos</h3>
            <p className="text-zinc-400 mb-6">
              {search || filterEstado || filterTipo 
                ? 'No se encontraron referidos con los filtros aplicados'
                : 'Comienza agregando tu primer referido'}
            </p>
            <button
              onClick={() => navigate(`/aliados/${aliado_id}/referidos/nuevo`)}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 rounded-lg hover:bg-[#C49F27] transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Nuevo Referido
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {referidos.map((referido) => {
              const tipoInfo = TIPOS_REFERIDO[referido.tipo_referido] || TIPOS_REFERIDO.otro;
              const estadoInfo = ESTADOS_REFERIDO[referido.estado] || ESTADOS_REFERIDO.registrado;
              const EstadoIcon = estadoInfo.icon;
              
              return (
                <div
                  key={referido.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-all cursor-pointer group"
                  onClick={() => navigate(`/aliados/${aliado_id}/referidos/${referido.id}`)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Info principal */}
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${tipoInfo.color}/20`}>
                        {referido.tipo_referido === 'mascota' ? (
                          <Heart className={`w-5 h-5 ${tipoInfo.color.replace('bg-', 'text-')}`} />
                        ) : (
                          <Users className={`w-5 h-5 ${tipoInfo.color.replace('bg-', 'text-')}`} />
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-display text-lg group-hover:text-[#D4AF37] transition-colors">{referido.nombre}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${tipoInfo.color}`}>
                            {tipoInfo.label}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                          {referido.telefono && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {referido.telefono}
                            </span>
                          )}
                          {referido.correo && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" />
                              {referido.correo}
                            </span>
                          )}
                          {referido.fecha_evento && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(referido.fecha_evento).toLocaleDateString('es-CO')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Valores y estado */}
                    <div className="flex flex-wrap items-center gap-6 lg:gap-8">
                      {referido.valor_cotizacion > 0 && (
                        <div className="text-center">
                          <p className="text-lg font-display">{formatPrice(referido.valor_cotizacion)}</p>
                          <p className="text-xs text-zinc-500">Total c/IVA</p>
                          <p className="text-xs text-zinc-600">
                            Base: {formatPrice(referido.valor_base_sin_iva || Math.round(referido.valor_cotizacion / 1.19))}
                          </p>
                        </div>
                      )}
                      
                      {referido.valor_comision > 0 && (
                        <div className="text-center">
                          <p className="text-lg font-display text-[#D4AF37]">{formatPrice(referido.valor_comision)}</p>
                          <p className="text-xs text-zinc-500">
                            Comisión ({referido.porcentaje_comision || tipoInfo.comision})
                          </p>
                          <p className="text-xs text-zinc-600">s/base sin IVA</p>
                        </div>
                      )}}
                      
                      <div className="flex items-center gap-2">
                        <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white ${estadoInfo.color}`}>
                          <EstadoIcon className="w-3.5 h-3.5" />
                          {estadoInfo.label}
                        </span>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/aliados/${aliado_id}/referidos/${referido.id}`);
                        }}
                        className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5 text-zinc-400" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Fechas importantes */}
                  {(referido.fecha_entrega_traje || referido.observaciones) && (
                    <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap gap-4 text-sm">
                      {referido.fecha_entrega_traje && (
                        <span className="text-zinc-400">
                          Entrega traje: <span className="text-white">{new Date(referido.fecha_entrega_traje).toLocaleDateString('es-CO')}</span>
                        </span>
                      )}
                      {referido.observaciones && (
                        <span className="text-zinc-500 italic truncate max-w-md">{referido.observaciones}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ReferidosList;
